<template>
  <div class="project-structure-page">
    <div class="header">
      <h1>Project Structure Management</h1>
      <p>
        Define reusable structure types and structure sets before creating projects.
        Projects will then select one structure set at creation time.
      </p>
    </div>

    <div class="management-grid">
      <section class="panel">
        <div class="panel-header">
          <h2>Structure Sets</h2>
          <div class="panel-actions">
            <span v-if="loadingSets" class="muted">Loading...</span>
            <button class="create-button" type="button" @click="openSetModal">Create Set</button>
          </div>
        </div>

        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Depth</th>
                <th>Active</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in structureSets" :key="`set-${item.id}`">
                <td>{{ item.name }}</td>
                <td>{{ computeSetDepth(item.id) }}</td>
                <td>{{ item.active ? 'Yes' : 'No' }}</td>
                <td class="actions-col">
                  <router-link
                    class="rule-builder-link"
                    :to="{ name: 'ProjectStructureSetBuilderPage', params: { setId: item.id } }"
                  >
                    Build Rules
                  </router-link>
                </td>
              </tr>
              <tr v-if="!loadingSets && !structureSets.length">
                <td colspan="4" class="empty-row">No structure sets found.</td>
              </tr>
            </tbody>
          </table>
        </div>

      </section>

      <section class="panel">
        <div class="panel-header">
          <h2>Structure Types</h2>
          <div class="panel-actions">
            <span v-if="loadingTypes" class="muted">Loading...</span>
            <button class="create-button" type="button" @click="openTypeModal">Create Type</button>
          </div>
        </div>

        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Experiment Parent</th>
                <th>Ordered</th>
                <th>Requires Location</th>
                <th>Active</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in structureTypes" :key="`type-${item.id}`">
                <td>{{ item.name }}</td>
                <td>{{ item.is_experiment_parent ? 'Yes' : 'No' }}</td>
                <td>{{ item.is_ordered ? 'Yes' : 'No' }}</td>
                <td>{{ item.requires_location ? 'Yes' : 'No' }}</td>
                <td>{{ item.active ? 'Yes' : 'No' }}</td>
                <td class="actions-col">
                  <button class="small-button" type="button" @click="openEditTypeModal(item)">Edit</button>
                </td>
              </tr>
              <tr v-if="!loadingTypes && !structureTypes.length">
                <td colspan="5" class="empty-row">No structure types found.</td>
              </tr>
            </tbody>
          </table>
        </div>

      </section>
    </div>

    <div
      v-if="showSetModal"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Create structure set"
      @click.self="closeSetModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h4>Create New Structure Set</h4>
          <button class="modal-close" type="button" aria-label="Close" @click="closeSetModal">×</button>
        </div>

        <form class="modal-form" @submit.prevent="submitSet">
          <div class="form-group">
            <label for="set-name">Name</label>
            <input id="set-name" v-model.trim="setForm.name" type="text" class="form-control" required />
          </div>
          <div class="modal-actions">
            <button class="secondary-button" type="button" @click="closeSetModal">Cancel</button>
            <button class="submit-button" type="submit" :disabled="submittingSet">
              {{ submittingSet ? 'Creating...' : 'Create Structure Set' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="showTypeModal"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="editingTypeId ? 'Edit structure type' : 'Create structure type'"
      @click.self="closeTypeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h4>{{ editingTypeId ? 'Edit Structure Type' : 'Create New Structure Type' }}</h4>
          <button class="modal-close" type="button" aria-label="Close" @click="closeTypeModal">×</button>
        </div>

        <form class="modal-form" @submit.prevent="submitType">
          <div class="form-group">
            <label for="type-name">Name</label>
            <input id="type-name" v-model.trim="typeForm.name" type="text" class="form-control" required />
          </div>
          <div class="checkbox-grid">
            <label><input v-model="typeForm.is_experiment_parent" type="checkbox" /> Experiment Parent</label>
            <label><input v-model="typeForm.is_ordered" type="checkbox" /> Ordered</label>
            <label><input v-model="typeForm.requires_location" type="checkbox" /> Requires Location</label>
            <label><input v-model="typeForm.allow_comments" type="checkbox" /> Allow Comments</label>
            <label><input v-model="typeForm.allow_files" type="checkbox" /> Allow Files</label>
          </div>
          <div class="modal-actions">
            <button class="secondary-button" type="button" @click="closeTypeModal">Cancel</button>
            <button class="submit-button" type="submit" :disabled="submittingType">
              {{ submittingType ? 'Saving...' : editingTypeId ? 'Save Changes' : 'Create Structure Type' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';

const defaultSetForm = () => ({
  name: '',
});

const defaultTypeForm = () => ({
  name: '',
  is_experiment_parent: false,
  is_ordered: false,
  requires_location: false,
  allow_comments: true,
  allow_files: true,
});

export default {
  name: 'ProjectStructurePage',
  data() {
    return {
      structureSets: [],
      structureTypes: [],
      loadingSets: false,
      loadingTypes: false,
      submittingSet: false,
      submittingType: false,
      showSetModal: false,
      showTypeModal: false,
      editingTypeId: null,
      setRulesMap: {},
      setForm: defaultSetForm(),
      typeForm: defaultTypeForm(),
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.refreshData();
  },
  methods: {
    openSetModal() {
      this.setForm = defaultSetForm();
      this.showSetModal = true;
    },

    closeSetModal() {
      if (this.submittingSet) {
        return;
      }
      this.showSetModal = false;
    },

    openTypeModal() {
      this.editingTypeId = null;
      this.typeForm = defaultTypeForm();
      this.showTypeModal = true;
    },

    openEditTypeModal(item) {
      this.editingTypeId = item.id;
      this.typeForm = {
        name: item.name,
        is_experiment_parent: !!item.is_experiment_parent,
        is_ordered: !!item.is_ordered,
        requires_location: !!item.requires_location,
        allow_comments: !!item.allow_comments,
        allow_files: !!item.allow_files,
      };
      this.showTypeModal = true;
    },

    closeTypeModal() {
      if (this.submittingType) {
        return;
      }
      this.editingTypeId = null;
      this.showTypeModal = false;
    },

    computeSetDepth(setId) {
      const rules = this.setRulesMap[setId];
      if (!rules || !rules.length) {
        return 1; // just Project
      }

      // Build childrenByParent: null parent key = direct children of Project
      const childrenByParent = {};
      for (const rule of rules) {
        const parentId = rule?.parent_type_id ?? rule?.parent_structure_type_id ?? rule?.parent_id ?? rule?.parent_type?.id ?? null;
        const childId = rule?.child_type_id ?? rule?.child_structure_type_id ?? rule?.child_id ?? rule?.child_type?.id ?? null;
        if (!childId) continue;
        const key = parentId ?? '__ROOT__';
        if (!childrenByParent[key]) childrenByParent[key] = [];
        childrenByParent[key].push(childId);
      }

      // DFS to find max chain depth; Project itself counts as 1
      const dfs = (key, visited) => {
        const children = childrenByParent[key] || [];
        if (!children.length) return 0;
        let max = 0;
        for (const child of children) {
          if (visited.has(child)) continue;
          visited.add(child);
          const d = dfs(child, visited);
          if (d > max) max = d;
          visited.delete(child);
        }
        return max + 1;
      };

      return 1 + dfs('__ROOT__', new Set());
    },

    buildCodeFromName(name) {
      return String(name || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64);
    },

    async refreshData() {
      await Promise.all([this.fetchStructureSets(), this.fetchStructureTypes()]);
    },

    normalizeCollection(payload, key) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (payload && Array.isArray(payload.items)) {
        return payload.items;
      }

      if (payload && Array.isArray(payload[key])) {
        return payload[key];
      }

      if (payload && payload.data && Array.isArray(payload.data.items)) {
        return payload.data.items;
      }

      if (payload && payload.data && Array.isArray(payload.data)) {
        return payload.data;
      }

      return [];
    },

    async fetchStructureSets() {
      this.loadingSets = true;
      try {
        const response = await apiService.getProjectStructureSets(this.loggedInUser.entra_id);
        this.structureSets = this.normalizeCollection(response, 'sets');
        // Fetch rules for each set in parallel so depth can be computed
        const rulesResults = await Promise.allSettled(
          this.structureSets.map((set) =>
            apiService.getProjectStructureSetRules(set.id, this.loggedInUser.entra_id)
              .then((r) => ({ id: set.id, rules: this.normalizeCollection(r, 'rules') }))
          )
        );
        const map = {};
        for (const result of rulesResults) {
          if (result.status === 'fulfilled') {
            map[result.value.id] = result.value.rules;
          }
        }
        this.setRulesMap = map;
      } catch (error) {
        this.toast.error('Failed to fetch structure sets.');
        this.structureSets = [];
      } finally {
        this.loadingSets = false;
      }
    },

    async fetchStructureTypes() {
      this.loadingTypes = true;
      try {
        const response = await apiService.getProjectStructureTypes(this.loggedInUser.entra_id);
        this.structureTypes = this.normalizeCollection(response, 'types');
      } catch (error) {
        this.toast.error('Failed to fetch structure types.');
        this.structureTypes = [];
      } finally {
        this.loadingTypes = false;
      }
    },

    async submitSet() {
      if (!this.setForm.name) {
        this.toast.error('Name is required for structure sets.');
        return;
      }

      const generatedCode = this.buildCodeFromName(this.setForm.name);
      if (!generatedCode) {
        this.toast.error('Could not generate a valid code from the set name.');
        return;
      }

      this.submittingSet = true;
      try {
        await apiService.createProjectStructureSet(
          { name: this.setForm.name, code: generatedCode, active: true },
          this.loggedInUser.entra_id
        );

        this.toast.success('Structure set created.');
        this.setForm = defaultSetForm();
        this.showSetModal = false;
        await this.fetchStructureSets();
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to create structure set.');
      } finally {
        this.submittingSet = false;
      }
    },

    async submitType() {
      if (!this.typeForm.name) {
        this.toast.error('Name is required for structure types.');
        return;
      }

      const payload = {
        name: this.typeForm.name,
        is_experiment_parent: !!this.typeForm.is_experiment_parent,
        is_ordered: !!this.typeForm.is_ordered,
        requires_location: !!this.typeForm.requires_location,
        allow_comments: !!this.typeForm.allow_comments,
        allow_files: !!this.typeForm.allow_files,
      };

      this.submittingType = true;
      try {
        if (this.editingTypeId) {
          await apiService.updateProjectStructureType(
            this.editingTypeId,
            payload,
            this.loggedInUser.entra_id
          );
          this.toast.success('Structure type updated.');
        } else {
          const generatedCode = this.buildCodeFromName(this.typeForm.name);
          if (!generatedCode) {
            this.toast.error('Could not generate a valid code from the type name.');
            return;
          }
          await apiService.createProjectStructureType(
            { ...payload, code: generatedCode, active: true },
            this.loggedInUser.entra_id
          );
          this.toast.success('Structure type created.');
        }

        this.editingTypeId = null;
        this.typeForm = defaultTypeForm();
        this.showTypeModal = false;
        await this.fetchStructureTypes();
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to save structure type.');
      } finally {
        this.submittingType = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/main.scss';

.project-structure-page {
  max-width: 1400px;
  margin: 20px auto;
  padding: 20px;

  .header {
    margin-bottom: 20px;

    h1 {
      margin-bottom: 8px;
      color: $primary-color;
    }

    p {
      margin: 0;
    }
  }
}

.management-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.panel {
  background: $secondary-color;
  border-radius: 8px;
  border: 1px solid rgba($primary-color, 0.18);
  padding: 16px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h2 {
    margin: 0;
    color: $primary-color;
  }

  .muted {
    color: rgba($primary-color, 0.8);
  }
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.create-button {
  padding: 8px 12px;
  font-size: 0.9rem;
}

.table-wrapper {
  overflow-x: auto;
  margin-bottom: 18px;

  table {
    width: 100%;
  }

  .empty-row {
    text-align: center;
  }
}

.actions-col {
  width: 150px;
  text-align: right;
}

.small-button {
  padding: 5px 10px;
  font-size: 0.85rem;
}

.rule-builder-link {
  color: $primary-color;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

.checkbox-row,
.checkbox-grid {
  margin-bottom: 1rem;

  label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.35rem;
  }
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2200;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.modal-content {
  width: min(560px, 100%);
  background: white;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid rgba($primary-color, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.modal-close {
  background: transparent;
  color: $primary-color;
  border: 1px solid rgba($primary-color, 0.2);
  border-radius: 4px;
  width: 32px;
  height: 32px;
  padding: 0;
  font-size: 1.2rem;
  line-height: 1;
}

.modal-form {
  .form-group {
    margin-bottom: 1rem;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.secondary-button {
  background: white;
  color: $primary-color;
  border: 1px solid rgba($primary-color, 0.4);
}

@media (max-width: 1100px) {
  .management-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .checkbox-grid {
    grid-template-columns: 1fr;
  }
}
</style>
